import type { EntityStateName } from '../config/tuning';

/**
 * design/gdd/character-visuals.md Core Rule #6 — groups Movement's
 * fine-grained sub-states so one clip spans all of them without restarting
 * on each sub-state transition (e.g. ATTACK_WINDUP -> ATTACK_ACTIVE must NOT
 * re-trigger the attack animation). Pure and framework-agnostic so it's
 * unit-testable without a renderer.
 */

const ATTACK_STATES: readonly EntityStateName[] = ['ATTACK_WINDUP', 'ATTACK_ACTIVE', 'ATTACK_RECOVERY'];
const PARRY_STATES: readonly EntityStateName[] = ['PARRY_WINDOW', 'PARRY_RECOVERY'];

export type AnimGroup =
  | 'IDLE' | 'MOVING' | 'SPRINTING' | 'ATTACK' | 'PARRY'
  | 'BLOCKING' | 'CROUCHING' | 'CASTING'
  | 'STAGGERED' | 'DEAD' | 'DISCONNECTED';

/**
 * KayKit Character Animations import (2026-08-25) — CROUCHING, BLOCKING and
 * CASTING are their own groups now.
 *
 * All three used to collapse into 'IDLE', each for the same documented reason:
 * the two clip libraries the game shipped with had no crouch, no guard stance
 * and no channelling pose, so the honest thing was to stand still. The
 * animation pack supplies all three (`Crouching`/`Sneaking`,
 * `Melee_Blocking`, `Ranged_Magic_Spellcasting`), so the collapse is gone and
 * `animation-map.ts` owns the clip choice per rig.
 *
 * FEINT_RECOVERY still folds into IDLE: a feint is a cancelled swing, and the
 * pack has no flinch-of-recovery pose. The cancel already reads through the
 * swing stopping early.
 */
export function animGroupOf(state: EntityStateName): AnimGroup {
  if (ATTACK_STATES.includes(state)) return 'ATTACK';
  if (PARRY_STATES.includes(state)) return 'PARRY';
  if (state === 'FEINT_RECOVERY') return 'IDLE';
  return state as AnimGroup;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
